import { forwardRef, useImperativeHandle, useState } from 'react';
import toast from 'components/Base/Notification';
import { ETabActiveKeyContent } from 'components/Shared/AnimalDetails/AnimalOtherInfo/AnimalOtherInfo.data';
import { ModalWidthEnum } from 'components/Base/Modal';
import { useConvertAnimalToPetMutation } from 'redux/apiSlice/animal.slice';
import { EText } from 'data/datas/genericData';
import { TRtkErrorType } from 'data/types/api.types';
import { TConvertToPetModalProps } from './ConvertToPetModal.types';
import AdaptiveModal from 'components/Base/Modal/AdaptiveModal/AdaptiveModal';
import { Text } from 'components/Base/Typography';
import { useAnimalTypeRetractionNotices } from 'components/Shared/AnimalTypeRetractionNotice/useAnimalTypeRetractionNotices';

const ConvertToPetModal = forwardRef(
  ({ animalType, onChangeTab }: TConvertToPetModalProps, ref) => {
    const [open, setOpen] = useState(false);
    const [animalId, setAnimalId] = useState('');
    useImperativeHandle(ref, () => ({
      open: (id: string) => {
        setOpen(true);
        setAnimalId(id);
      },
    }));

    const [convertAnimalToPet, { isLoading }] = useConvertAnimalToPetMutation();
    const { showRetractionNotices, retractionNoticeModal } =
      useAnimalTypeRetractionNotices();

    const handleSubmit = async () => {
      try {
        const result = await convertAnimalToPet({ animalId }).unwrap();
        setOpen(false);
        // PP-10057: converting to a type the property does not manage retracts the application
        // there. Defer the tab switch until the tenant has read which residency was affected.
        const awaitingAcknowledgement = showRetractionNotices(
          result?.animalTypeRetractionNotices,
          () => onChangeTab?.(ETabActiveKeyContent.CONVERSION),
        );
        if (!awaitingAcknowledgement && onChangeTab) {
          onChangeTab(ETabActiveKeyContent.CONVERSION);
        }
        toast({
          description: 'Animal has been successfully converted',
          type: 'success',
        });
      } catch (e) {
        setOpen(false);
        toast({
          description:
            (e as TRtkErrorType)?.data?.message || EText.DEFAULT_ERROR,
          type: 'error',
        });
      }
    };

    return (
      <>
        {retractionNoticeModal}
        <AdaptiveModal
          title="Converting"
          size={ModalWidthEnum.Small}
          visible={open}
          destroyOnClose
          onOk={handleSubmit}
          okText="Convert"
          onCancel={() => setOpen(false)}
          confirmLoading={isLoading}
          cancelText="Cancel"
        >
          <Text>
            Are you sure you want to convert your {animalType} animal to Pet
            animal?
          </Text>
        </AdaptiveModal>
      </>
    );
  },
);

export default ConvertToPetModal;
